import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import Footer from "@/components/landing/Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Introduction",
    body: (
      <>
        Welcome to SHYFT Mobile (<span className="text-white">&ldquo;App&rdquo;</span>),
        operated by{" "}
        <span className="text-white">SHYFT Coalition LLC</span> (
        <span className="text-white">&ldquo;we,&rdquo; &ldquo;us,&rdquo;</span> or{" "}
        <span className="text-white">&ldquo;our&rdquo;</span>). We are
        committed to protecting user privacy. This Privacy Policy explains
        how data is handled within the App. By using the App, you agree to
        the terms outlined herein.
      </>
    ),
  },
  {
    n: "02",
    title: "Non-Custodial Architecture & Financial Data",
    body: (
      <>
        <p>
          The App provides an administrative expense and tax deduction
          tracking tool.
        </p>
        <ul className="mt-4 space-y-3">
          <li>
            <span className="text-white font-medium">
              Non-Custodial Status:
            </span>{" "}
            We do not provide financial, banking, investment, or custodial
            services. We do not hold, manage, store, or have access to
            your funds, bank accounts, or financial assets.
          </li>
          <li>
            <span className="text-white font-medium">Local Processing:</span>{" "}
            All financial information input into the expense tracker (such
            as deductions, receipt images, and dollar amounts) is processed
            locally to assist you with administrative organization.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "03",
    title: "Information We Collect and How We Use It",
    body: (
      <>
        <p>
          To provide core application functionalities, the App utilizes
          specific device permissions:
        </p>
        <ul className="mt-4 space-y-3">
          <li>
            <span className="text-white font-medium">
              Camera &amp; Media Library:
            </span>{" "}
            Used strictly when you explicitly choose to capture or upload
            images of receipts to attach to expense tracking lines.
          </li>
          <li>
            <span className="text-white font-medium">Local Storage:</span>{" "}
            Used to temporarily store app configurations and cached expense
            summaries locally on your device.
          </li>
          <li>
            <span className="text-white font-medium">Notifications:</span>{" "}
            Used to deliver critical signal alerts and system updates to
            your device.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "04",
    title: "Data Sharing and Third Parties",
    body: (
      <>
        We do not sell, trade, or rent your personal or financial tracking
        data to third parties. Data is only utilized locally on your device
        or transmitted securely to your designated accounting infrastructure
        based entirely on your explicit in-app commands.
      </>
    ),
  },
  {
    n: "05",
    title: "Security",
    body: (
      <>
        We implement industry-standard security protocols to protect data
        in transit and at rest. However, because our architecture is
        non-custodial, the ultimate security of your local data and account
        credentials rests on maintaining the security of your personal
        mobile device.
      </>
    ),
  },
  {
    n: "06",
    title: "Changes to This Policy",
    body: (
      <>
        We may update this Privacy Policy from time to time. We will notify
        you of any changes by updating the{" "}
        <span className="text-white">&ldquo;Last Updated&rdquo;</span> date
        at the top of this policy.
      </>
    ),
  },
  {
    n: "07",
    title: "Contact Us",
    body: (
      <>
        If you have any questions about this Privacy Policy, please contact
        us at:{" "}
        <a
          href={`mailto:${LINKS.supportEmail}`}
          className="text-gold hover:text-white transition"
          data-testid="privacy-support-email"
        >
          {LINKS.supportEmail}
        </a>
        .
      </>
    ),
  },
];

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy — SHYFT Mobile";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen" data-testid="privacy-page">
      {/* Minimal header */}
      <header className="relative z-10 max-w-5xl mx-auto px-6 pt-10 md:pt-14 flex items-center justify-between">
        <Link
          to="/"
          data-testid="privacy-home-link"
          className="flex items-center gap-3 group"
        >
          <span className="brand-seal w-10 h-10 overflow-hidden">
            <img
              src={IMAGES.brandLogo}
              alt="SHYFT Coalition™"
              className="w-9 h-9 object-contain rounded-full"
            />
          </span>
          <span className="font-serif-display text-lg sm:text-xl text-white tracking-wider">
            SHYFT<span className="text-gold ml-1.5">Coalition</span>
          </span>
        </Link>

        <Link
          to="/"
          data-testid="privacy-back-link"
          className="btn-outline-gold !py-2 !px-4 text-xs sm:text-sm"
        >
          <ArrowLeft size={14} />
          Back to vault
        </Link>
      </header>

      {/* Spotlights */}
      <div
        aria-hidden
        className="vault-spotlight"
        style={{ top: "10%", left: "-8%", width: 460, height: 460, opacity: 0.5 }}
      />
      <div
        aria-hidden
        className="vault-spotlight-cyan"
        style={{ top: "25%", right: "-8%", width: 520, height: 520, opacity: 0.45 }}
      />

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-12 md:pt-16 pb-24">
        {/* Title block */}
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[rgba(201,162,91,0.35)] bg-[#14110e]/70 backdrop-blur">
          <ShieldCheck size={14} className="text-gold" />
          <span className="eyebrow !text-[#e7c98a]">SHYFT Mobile · Legal</span>
          <span className="ml-1 status-dot" aria-hidden />
        </div>

        <h1 className="font-serif-display mt-7 text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] text-white">
          <span className="text-gradient-gold">Privacy</span> Policy
        </h1>

        <p className="mt-4 eyebrow !text-[11px] !text-[#b8b3aa]">
          Last Updated: May 20, 2026
        </p>

        <div className="vault-divider mt-10" />

        {/* Sections */}
        <div className="mt-12 space-y-12">
          {SECTIONS.map(({ n, title, body }) => (
            <section
              key={n}
              data-testid={`privacy-section-${n}`}
              className="scroll-mt-24"
            >
              <div className="flex items-baseline gap-4">
                <span className="font-serif-display text-3xl text-gold leading-none">
                  {n}
                </span>
                <h2 className="font-serif-display text-2xl md:text-[1.75rem] leading-tight text-white">
                  {title}
                </h2>
              </div>
              <div className="mt-4 ml-0 sm:ml-[3.25rem] text-[#cfc8b9] text-base leading-[1.75]">
                {body}
              </div>
            </section>
          ))}
        </div>

        {/* Closing card */}
        <div className="mt-16 rounded-[2rem] border border-[rgba(201,162,91,0.25)] bg-[#14110e]/70 backdrop-blur p-6 md:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="eyebrow">Need a human?</div>
            <p className="text-[#cfc8b9] mt-1.5 text-sm">
              Reach the Coalition concierge directly.
            </p>
          </div>
          <a
            href={`mailto:${LINKS.supportEmail}`}
            data-testid="privacy-bottom-support-email"
            className="btn-outline-gold !py-2.5 !px-5 text-sm"
          >
            {LINKS.supportEmail}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
